import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import ApiURl from "../Paths/Api";

// SVG Icons as React components
const SearchIcon = () => (
    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

const RestrictMeeting = () => {
    const [meetings, setMeetings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const navigate = useNavigate();

    // Fetch meetings
    const fetchMeetings = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const phone = localStorage.getItem('phone');
            if (!phone) {
                throw new Error("Phone number not found in local storage");
            }

            const response = await fetch(`${ApiURl}/get_meetings.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ phone }),
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const result = await response.json();
            console.log("Fetched meetings data:", result);
            if (result.success) {
                setMeetings(result.data);
            } else {
                throw new Error(result.message || "Failed to fetch meetings");
            }
        } catch (err) {
            setError(err.message);
            console.error("Error fetching meetings:", err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMeetings();
    }, [fetchMeetings]);

    // Toggle dark mode
    const toggleDarkMode = () => {
        setIsDarkMode(!isDarkMode);
        document.documentElement.classList.toggle('dark');
    };

    // Handle restrict/unrestrict action
    const toggleRestriction = async (meetingId, currentRestricted) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${ApiURl}/restrict_meeting.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    meetingId,
                    restrict: currentRestricted === '1' ? '0' : '1',
                }),
            });

            const result = await response.json();
            console.log("Restriction response:", result);
            if (result.success) {
                // Update the meetings state to reflect the new restriction status
                setMeetings((prevMeetings) =>
                    prevMeetings.map((meeting) =>
                        meeting.id === meetingId
                            ? { ...meeting, restricted: currentRestricted === '1' ? '0' : '1' }
                            : meeting
                    )
                );
            } else {
                throw new Error(result.message || "Failed to update restriction status");
            }
        } catch (err) {
            setError(err.message);
            console.error("Error updating restriction:", err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'} font-sans`}>
            {/* Navbar */}
            <nav className="bg-gradient-to-r from-teal-600 to-cyan-600 dark:from-teal-800 dark:to-cyan-800 text-white p-4 shadow-xl fixed w-full z-20">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center">
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="mr-4 md:hidden focus:outline-none hover:bg-teal-700 dark:hover:bg-teal-900 p-2 rounded-full transition-all duration-300"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                        <h1 className="text-2xl font-bold tracking-tight">Event Manager</h1>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={toggleDarkMode}
                            className="p-2 rounded-full hover:bg-teal-700 dark:hover:bg-teal-900 transition-all duration-300"
                        >
                            {isDarkMode ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                </svg>
                            )}
                        </button>
                        <button
                            onClick={() => navigate(-1)}
                            className="px-4 py-2 bg-red-500 rounded-full hover:bg-red-600 dark:hover:bg-red-700 transition-all duration-300 text-sm font-medium shadow-md hover:scale-105"
                        >
                            Back
                        </button>
                    </div>
                </div>
            </nav>

            {/* Sidebar */}
            <div
                className={`fixed top-14 left-0 h-[calc(100vh-3.5rem)] bg-gradient-to-b from-teal-600 to-teal-800 text-white w-16 flex flex-col items-center py-4 space-y-4 transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:w-16 z-20`}
            >
                <button
                    className="p-2 rounded-full bg-teal-700 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
                    title="Dashboard"
                    onClick={() => navigate('/dashboard')}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Dashboard</span>
                </button>
                <button
                    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
                    title="Create Meeting"
                    onClick={() => navigate('/create-meeting')}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Create Meeting</span>
                </button>
                <button
                    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
                    title="View Meetings"
                    onClick={() => navigate('/view-meetings')}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Reports</span>
                </button>
                <button
                    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
                    title="Send Invites"
                    onClick={() => navigate('/send-invites')}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
                    </svg>
                    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Invite</span>
                </button>
                <button
                    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
                    title="Search Meetings"
                    onClick={() => navigate('/search-form')}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Search</span>
                </button>
                <button
                    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
                    title="Send Invite Without Pay"
                    onClick={() => navigate('/adminsend')}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
                    </svg>
                    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Send Invite</span>
                </button>
                <button
                    className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
                    title="Restrict Meeting"
                    onClick={() => navigate('/restrict-meeting')}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.104-.896 2-2 2s-2-.896-2-2 2-4 2-4 2 2.896 2 4zm0 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm-6 7c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm12 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4z" />
                    </svg>
                    <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Restrict Meeting</span>
                </button>
            </div>

            {/* Main Content */}
            <div className="pt-20 md:pl-24 p-6 max-w-7xl mx-auto transition-all duration-500">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-semibold text-gray-900 dark:text-white animate-fade-in">Restrict Meetings</h1>
                    <button
                        onClick={fetchMeetings}
                        className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-800 transition-all duration-300 text-sm font-medium shadow-lg hover:shadow-xl hover:scale-105"
                    >
                        Refresh
                    </button>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-xl border border-red-200 dark:border-red-700 text-sm animate-slide-in shadow-md">
                        {error}
                        <button
                            onClick={fetchMeetings}
                            className="ml-3 text-red-600 dark:text-red-400 underline hover:text-red-800 dark:hover:text-red-200 font-medium"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl animate-fade-in border border-gray-200 dark:border-gray-700">
                    {/* Loading State */}
                    {isLoading ? (
                        <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500 mx-auto"></div>
                            <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm">Loading meetings...</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-teal-50 dark:bg-teal-900/50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">S.No</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Title</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Purpose</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Location</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Time</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Restricted</th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {meetings.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="px-6 py-6 text-center text-gray-500 dark:text-gray-400 text-sm font-medium">No meetings found</td>
                                        </tr>
                                    ) : (
                                        meetings.map((meeting, index) => (
                                            <tr key={meeting.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200">
                                                <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{index + 1}</td>
                                                <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{meeting.title || "Untitled"}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{meeting.purpose || "N/A"}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{meeting.location || "N/A"}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{meeting.time ? new Date(meeting.time.replace(" ", "T")).toLocaleString() : "N/A"}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 capitalize">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${meeting.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'}`}>
                                                        {meeting.status || "N/A"}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${meeting.restricted === '1' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'}`}>
                                                        {meeting.restricted === '1' ? 'Restricted' : 'Unrestricted'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    {meeting.status !== 'completed' && (
                                                        <button
                                                            onClick={() => toggleRestriction(meeting.id, meeting.restricted)}
                                                            className={`px-4 py-2 rounded-lg text-sm font-medium shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 ${meeting.restricted === '1'
                                                                    ? 'bg-green-600 text-white hover:bg-green-700 dark:hover:bg-green-800'
                                                                    : 'bg-red-600 text-white hover:bg-red-700 dark:hover:bg-red-700'
                                                                }`}
                                                            disabled={isLoading}
                                                        >
                                                            {meeting.restricted === '1' ? 'Unrestrict' : 'Restrict'}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Tailwind Animation Styles */}
            <style jsx>{`
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        .animate-slide-in {
          animation: slide-in 0.4s ease-out;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
        </div>
    );
};

export default RestrictMeeting;